// Skips the MSAA resolves Pixi 8.9.2 does in the middle of a filter group.
//
// A filtered container renders into a multisampled render target (filter
// antialias on), which Pixi resolves (a blitFramebuffer of the whole target)
// when the group pops, before its filter reads it. But FilterSystem.applyFilter
// also resolves the target it drew into after every filter pass, so each
// filter nested inside a multisampled group (a shadow inside a window that is
// fading in) resolves the whole parent target once more. A 4K window group
// with 21 shadows resolved its target ~43 extra times a frame (Accent check,
// clip entry: 87 ms -> 53 ms per frame without them).
//
// Those resolves are never read: the parent is resolved again when it pops,
// and Pixi resolves a target itself before copying from it (back textures).
// So a pass whose output is its parent group's target skips the resolve. The
// root target keeps it. Pixels are identical (GPU study 2026-10-09 and the
// export check 2026-10-10: 0 delta on every compared frame), so it is
// installed once, on FilterSystem itself (src/index.js), for every renderer:
// the editor canvases, export and everything else.
//
// It reads FilterSystem's private stack (`_filterStack`, `_filterStackIndex`,
// `previousRenderSurface`) and relies on applyFilter resolving through
// renderTarget.finishRenderPass, all checked against Pixi 8.9.2 only: any other
// version throws at install, so an upgrade re-checks them instead of silently
// resolving wrong (or not skipping at all).
import { VERSION } from 'pixi.js'

const PATCHED_PIXI_VERSION = '8.9.2'

export function installDeferNestedFilterResolve(FilterSystem) {
  if (VERSION !== PATCHED_PIXI_VERSION) {
    throw new Error(`deferNestedFilterResolve is written against pixi.js ${PATCHED_PIXI_VERSION}, found ${VERSION}: re-check FilterSystem.applyFilter`)
  }
  const applyFilter = FilterSystem.prototype.applyFilter
  const skip = () => {}

  FilterSystem.prototype.applyFilter = function applyFilterDeferringResolve(filter, input, output, clear) {
    const renderTargets = this.renderer.renderTarget
    const intoParentGroup = output === this._filterStack[this._filterStackIndex].previousRenderSurface
      && renderTargets.getRenderTarget(output) !== renderTargets.rootRenderTarget
    if (!intoParentGroup) return applyFilter.call(this, filter, input, output, clear)
    const finishRenderPass = renderTargets.finishRenderPass
    renderTargets.finishRenderPass = skip
    try {
      return applyFilter.call(this, filter, input, output, clear)
    } finally {
      renderTargets.finishRenderPass = finishRenderPass
    }
  }
}
